import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { Download, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const ExportModal = ({ open, onClose }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectAll, setSelectAll] = useState(true); // Track "All Projects" checkbox state separately
  const [startDate, setStartDate] = useState(format(new Date(new Date().setDate(1)), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [formatType, setFormatType] = useState('csv');
  const [billableOnly, setBillableOnly] = useState(false);
  const [includeDescriptions, setIncludeDescriptions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState({ count: 0, hours: 0 });

  useEffect(() => {
    if (open) {
      fetchProjects();
    }
  }, [open]);

  useEffect(() => {
    if (open && projects.length > 0) {
      fetchPreview();
    }
  }, [open, startDate, endDate, selectedProjects, selectAll, billableOnly, projects.length]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const fetchedProjects = response.data || [];
      setProjects(fetchedProjects);
      // Initialize with all projects selected and "All Projects" checked
      setSelectedProjects(fetchedProjects.map(p => p.id));
      setSelectAll(true);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchPreview = async () => {
    try {
      const response = await axios.get(`${API}/time-entries`, {
        params: {
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate).toISOString()
        }
      });

      let filtered = response.data;
      
      // Filter by selected projects if not all are selected
      if (!selectAll && selectedProjects.length > 0) {
        filtered = filtered.filter(e => selectedProjects.includes(e.project_id));
      } else if (!selectAll && selectedProjects.length === 0) {
        // If no projects selected, return empty
        filtered = [];
      }
      // If selectAll is true, don't filter (include all projects)

      if (billableOnly) {
        // Filter for billable projects
        const billableProjectIds = projects.filter(p => p.is_billable).map(p => p.id);
        filtered = filtered.filter(e => billableProjectIds.includes(e.project_id));
      }

      const totalMinutes = filtered.reduce((sum, e) => sum + e.duration_minutes, 0);
      setPreview({ count: filtered.length, hours: (totalMinutes / 60).toFixed(2) });
    } catch (error) {
      console.error('Failed to fetch preview:', error);
    }
  };

  const toggleProject = (projectId) => {
    setSelectedProjects(prev => {
      let updated;
      if (prev.includes(projectId)) {
        updated = prev.filter(id => id !== projectId);
      } else {
        updated = [...prev, projectId];
      }
      
      // Update "All Projects" checkbox based on selection
      // If all projects are selected, check "All Projects"
      // Otherwise, uncheck "All Projects"
      // Use functional update to ensure we have latest projects state
      setSelectAll(prevSelectAll => {
        const allSelected = updated.length === projects.length && projects.length > 0;
        return allSelected;
      });
      
      return updated;
    });
  };

  const toggleAllProjects = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    
    if (newSelectAll) {
      // Select all projects
      setSelectedProjects(projects.map(p => p.id));
    } else {
      // Clear all selections
      setSelectedProjects([]);
    }
  };

  const exportCSV = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/time-entries`, {
        params: {
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate).toISOString()
        }
      });

      let entries = response.data;
      
      // Filter by selected projects if not all are selected
      if (!selectAll && selectedProjects.length > 0) {
        entries = entries.filter(e => selectedProjects.includes(e.project_id));
      } else if (!selectAll && selectedProjects.length === 0) {
        // If no projects selected, return empty
        entries = [];
      }
      // If selectAll is true, don't filter (include all projects)

      if (billableOnly) {
        const billableProjectIds = projects.filter(p => p.is_billable).map(p => p.id);
        entries = entries.filter(e => billableProjectIds.includes(e.project_id));
      }

      // Create CSV
      const headers = ['Date', 'Start Time', 'End Time', 'Duration (hours)', 'Project', 'Description', 'Entry Type', 'Billable', 'Attendees'];

      let csv = headers.join(',') + '\n';

      entries.forEach(entry => {
        const entryDate = new Date(entry.date);
        const endDate = new Date(entryDate.getTime() + entry.duration_minutes * 60000);
        const project = projects.find(p => p.id === entry.project_id);
        
        const row = [
          format(entryDate, 'yyyy-MM-dd'),
          format(entryDate, 'HH:mm'),
          format(endDate, 'HH:mm'),
          (entry.duration_minutes / 60).toFixed(2),
          `"${entry.project_name || 'N/A'}"`,
          `"${(entry.notes || '').replace(/"/g, '""')}"`,
          entry.entry_type,
          project?.is_billable ? 'Yes' : 'No',
          `"${(entry.attendees || []).join(', ')}"`
        ];

        csv += row.join(',') + '\n';
      });

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `logly-timesheet-${startDate}-${endDate}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success('CSV exported successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to export CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/time-entries`, {
        params: {
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate).toISOString()
        }
      });

      let entries = response.data;
      
      // Filter by selected projects if not all are selected
      if (!selectAll && selectedProjects.length > 0) {
        entries = entries.filter(e => selectedProjects.includes(e.project_id));
      } else if (!selectAll && selectedProjects.length === 0) {
        // If no projects selected, return empty
        entries = [];
      }
      // If selectAll is true, don't filter (include all projects)

      if (billableOnly) {
        const billableProjectIds = projects.filter(p => p.is_billable).map(p => p.id);
        entries = entries.filter(e => billableProjectIds.includes(e.project_id));
      }

      // Create PDF
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.text('Logly Timesheet', 14, 20);
      doc.setFontSize(12);
      doc.text(`${format(new Date(startDate), 'MMM dd, yyyy')} to ${format(new Date(endDate), 'MMM dd, yyyy')}`, 14, 30);

      // Table
      const tableHeaders = [['Date', 'Project', 'Duration (h)', 'Type', 'Description']];

      const tableData = entries.map(entry => {
        try {
          const entryDate = new Date(entry.date);
          const project = projects.find(p => p.id === entry.project_id);
          
          return [
            format(entryDate, 'MMM dd, yyyy'),
            entry.project_name || 'N/A',
            (entry.duration_minutes / 60).toFixed(2),
            entry.entry_type || 'work',
            includeDescriptions ? ((entry.notes || 'No description').substring(0, 60) + (entry.notes && entry.notes.length > 60 ? '...' : '')) : '-'
          ];
        } catch (err) {
          console.error('Error processing entry:', entry, err);
          return [
            'Invalid date',
            entry.project_name || 'N/A',
            (entry.duration_minutes / 60).toFixed(2),
            entry.entry_type || 'work',
            'Error'
          ];
        }
      });

      autoTable(doc, {
        startY: 40,
        head: tableHeaders,
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [6, 182, 212], // cyan-500
          textColor: 255,
          fontStyle: 'bold'
        },
        styles: { 
          fontSize: 9,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 30 }, // Date
          1: { cellWidth: 40 }, // Project
          2: { cellWidth: 25 }, // Duration
          3: { cellWidth: 25 }, // Type
          4: { cellWidth: 60 }  // Description
        }
      });

      // Totals
      const totalMinutes = entries.reduce((sum, e) => sum + e.duration_minutes, 0);
      const totalHours = (totalMinutes / 60).toFixed(2);
      
      const finalY = doc.lastAutoTable.finalY || 40;
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Total Hours: ${totalHours}`, 14, finalY + 15);
      doc.text(`Total Entries: ${entries.length}`, 14, finalY + 25);

      // Project breakdown
      const byProject = {};
      entries.forEach(entry => {
        const projName = entry.project_name || 'No Project';
        byProject[projName] = (byProject[projName] || 0) + entry.duration_minutes;
      });

      let yPos = finalY + 40;
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Hours by Project:', 14, yPos);
      doc.setFont(undefined, 'normal');
      yPos += 8;
      
      Object.entries(byProject).forEach(([proj, mins]) => {
        if (yPos > 270) { // Check if we need a new page
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(10);
        doc.text(`${proj}: ${(mins / 60).toFixed(2)}h`, 20, yPos);
        yPos += 6;
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Generated by Logly - Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      // Download
      doc.save(`logly-timesheet-${format(new Date(startDate), 'yyyy-MM-dd')}-${format(new Date(endDate), 'yyyy-MM-dd')}.pdf`);

      toast.success('PDF exported successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to export PDF:', error);
      console.error('Error details:', error.message, error.stack);
      toast.error(`Failed to export PDF: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (formatType === 'csv') {
      exportCSV();
    } else {
      exportPDF();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal Panel */}
        <Dialog.Panel className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[95vw] md:max-w-[550px] max-h-[85vh] md:max-h-[85vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700 transition-colors" data-testid="export-modal">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10 transition-colors">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors">Export Timesheet</Dialog.Title>
              <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400 mt-1 transition-colors">Download your time entries as CSV or PDF</Dialog.Description>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Select Date Range</label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label htmlFor="start-date" className="text-xs text-gray-600 dark:text-gray-400 block mb-1 transition-colors">From</label>
                  <input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate}
                    className="flex h-9 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400"
                    data-testid="export-start-date"
                  />
                </div>
                <div>
                  <label htmlFor="end-date" className="text-xs text-gray-600 dark:text-gray-400 block mb-1 transition-colors">To</label>
                  <input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    className="flex h-9 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400"
                    data-testid="export-end-date"
                  />
                </div>
              </div>
          </div>

          {/* Project Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Select Projects</label>
            <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-gray-50 dark:bg-gray-700/50 transition-colors">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="all-projects"
                  checked={selectAll}
                  onChange={toggleAllProjects}
                  className="w-4 h-4 text-cyan-500 rounded transition-colors"
                  data-testid="all-projects-checkbox"
                />
                <label htmlFor="all-projects" className="cursor-pointer font-semibold text-gray-900 dark:text-gray-100 transition-colors">All Projects</label>
              </div>
              {projects.map(proj => (
                <div key={proj.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`proj-${proj.id}`}
                    checked={selectAll || selectedProjects.includes(proj.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      // If "All Projects" is checked and user unchecks this project, uncheck "All Projects" first
                      if (selectAll) {
                        setSelectAll(false);
                        // Set selectedProjects to all except this one
                        setSelectedProjects(projects.filter(p => p.id !== proj.id).map(p => p.id));
                      } else {
                        toggleProject(proj.id);
                      }
                    }}
                    className="w-4 h-4 text-cyan-500 rounded transition-colors"
                    data-testid={`project-checkbox-${proj.id}`}
                  />
                  <label 
                    htmlFor={`proj-${proj.id}`} 
                    className="cursor-pointer text-gray-900 dark:text-gray-100 transition-colors"
                  >
                    {proj.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Include</label>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="billable-only"
                checked={billableOnly}
                onChange={(e) => setBillableOnly(e.target.checked)}
                className="w-4 h-4 text-cyan-500 rounded transition-colors"
                data-testid="billable-only-checkbox"
              />
              <label htmlFor="billable-only" className="cursor-pointer text-gray-900 dark:text-gray-100 transition-colors">Billable entries only</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="include-descriptions"
                checked={includeDescriptions}
                onChange={(e) => setIncludeDescriptions(e.target.checked)}
                className="w-4 h-4 text-cyan-500 rounded transition-colors"
                data-testid="include-descriptions-checkbox"
              />
              <label htmlFor="include-descriptions" className="cursor-pointer text-gray-900 dark:text-gray-100 transition-colors">Include descriptions</label>
            </div>
          </div>

          {/* Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">Export Format</label>
            <div className="mt-2 space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="format-csv"
                  name="format"
                  value="csv"
                  checked={formatType === 'csv'}
                  onChange={(e) => setFormatType(e.target.value)}
                  className="w-4 h-4 text-cyan-500 transition-colors"
                  data-testid="format-csv"
                />
                <label htmlFor="format-csv" className="cursor-pointer text-gray-900 dark:text-gray-100 transition-colors">CSV</label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="format-pdf"
                  name="format"
                  value="pdf"
                  checked={formatType === 'pdf'}
                  onChange={(e) => setFormatType(e.target.value)}
                  className="w-4 h-4 text-cyan-500 transition-colors"
                  data-testid="format-pdf"
                />
                <label htmlFor="format-pdf" className="cursor-pointer text-gray-900 dark:text-gray-100 transition-colors">PDF</label>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-cyan-50 dark:bg-cyan-900/20 p-3 rounded-lg border border-cyan-200 dark:border-cyan-800 transition-colors">
            <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors">
              Preview: <span className="font-semibold">{preview.count} entries</span>, <span className="font-semibold">{preview.hours} hours</span>
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              variant="outline"
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={loading || preview.count === 0}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-4 py-2 rounded-lg disabled:opacity-50 min-h-[44px] flex items-center justify-center"
              data-testid="export-button"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exporting...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Export</>
              )}
            </button>
          </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ExportModal;